import { useOpenState } from "@ext/enterprise/components/admin/hooks/useOpenState";
import { useMemberAccessDraft } from "@ext/enterprise/components/admin/settings/members/hooks/useMemberAccessDraft";
import {
	emailKey,
	getAccessRowId,
	type MemberAccess,
	type MemberAggregate,
} from "@ext/enterprise/components/admin/settings/members/model/Member";
import type { RoleRules } from "@ext/enterprise/components/admin/settings/members/model/roleRules";
import { useRowSelectionWithData } from "@ext/enterprise/components/admin/ui-kit/table/useRowSelection";
import { useCallback, useMemo, useState } from "react";

interface UseMemberAccessTableArgs {
	email: string;
	aggregate: MemberAggregate;
	roleRules: RoleRules;
}

export const useMemberAccessTable = ({ email, aggregate, roleRules }: UseMemberAccessTableArgs) => {
	const originalAccesses = useMemo(
		() => (email ? (aggregate.userAccesses.get(emailKey(email)) ?? []) : []),
		[email, aggregate],
	);

	const [accessRowsMap, setAccessRowsMap] = useState(() => {
		const res = new Map<string, MemberAccess>();
		originalAccesses.forEach((x) => {
			res.set(x.resourceId, x);
		});
		return res;
	});

	const accessPickerState = useOpenState({ keyBase: "access" });

	const {
		remove: removeAccesses,
		add: addAccesses,
		columns: accessColumns,
		validate: validateAccesses,
		rowVersions: accessRowVersions,
		demoteToReader: demoteAccessesToReader,
	} = useMemberAccessDraft({
		rowsMap: accessRowsMap,
		setRowsMap: setAccessRowsMap,
		roleRules,
	});

	const accessRows = useMemo(() => [...accessRowsMap.values()], [accessRowsMap]);

	const accessPreselected = useMemo(() => new Set(accessRows.map((x) => x.resourceId)), [accessRows]);

	const {
		rowSelection: accessSelection,
		setRowSelection: setAccessSelection,
		selectedRows: selectedAccesses,
	} = useRowSelectionWithData(accessRows, getAccessRowId);

	const removeSelectedAccesses = useCallback(() => {
		removeAccesses(selectedAccesses);
	}, [selectedAccesses, removeAccesses]);

	return {
		originalAccesses,
		accessRows,
		accessColumns,
		accessRowVersions,
		validateAccesses,
		accessPreselected,
		accessSelection,
		setAccessSelection,
		selectedAccesses,
		removeSelectedAccesses,
		accessPickerState,
		addAccesses,
		demoteAccessesToReader,
	};
};
